import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { catchError, throwError } from 'rxjs';
import { ErrorModel } from 'app/shared/error.model';
import { ErrorCodes } from 'app/shared/error-codes';
import type { UserSignUpDto, UserSignUp, UserSignInResponse, UserUpdate, User, ResetPassword } from './user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiBase + '/auth/v1/users';
  private passwordApiUrl = environment.apiBase + '/auth/v1/password/request';

  signUp(userSignUp: UserSignUpDto) {
    return this.http.post<UserSignUp>(this.apiUrl + '/sign-up', userSignUp).pipe(
      catchError((errorRes: HttpErrorResponse) => {
        const error: ErrorModel = errorRes.error;
        const errorMessage: string = this.getSignUpErrorMessage(error);

        return throwError(() => errorMessage);
      }),
    );
  }

  signIn(email: string, password: string) {
    return this.http.post<UserSignInResponse>(this.apiUrl + '/sign-in', {
      email,
      password,
    });
  }

  updateUser(userUpdate: Partial<UserUpdate>) {
    return this.http.patch<Partial<UserUpdate>>(this.apiUrl, userUpdate);
  }

  refreshAccessToken(refreshToken: string) {
    return this.http.post<{
      accessToken: string;
      refreshToken: string;
    }>(this.apiUrl + '/refresh-tokens', { refreshToken });
  }

  logout() {
    return this.http.post<{ message: string }>(this.apiUrl + '/logout', null);
  }

  getAuthorizedUser() {
    return this.http.get<User>(this.apiUrl);
  }

  forgotPassword(method: 'email' | 'phone', address: string) {
    return this.http.post<ResetPassword>(`${this.apiUrl}/forgot-password/${method}`, { address });
  }

  confirmResetPassword(requestId: string, code: string) {
    return this.http.put<{ message: string }>(`${this.passwordApiUrl}/${requestId}/confirm`, { code });
  }

  resendConfirmationCode(requestId: string) {
    return this.http.post<{ message: string }>(`${this.passwordApiUrl}/${requestId}/code`, null);
  }

  setNewPassword(requestId: string, newPassword: string) {
    return this.http.put<null>(`${this.apiUrl}/set-password?requestId=${requestId}`, { newPassword });
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.http.post<null>(`${this.apiUrl}/change-password`, { oldPassword, newPassword });
  }

  deleteAccount() {
    return this.http.delete<{ message: string }>(`${this.apiUrl}`);
  }

  private getSignUpErrorMessage(error: ErrorModel): string {
    const fieldErrorMessages: { [key: string]: string } = {
      email: 'Email already exists.',
      phone: 'Phone number already exists.',
      user_name: 'Username already exists.',
    };
    if (error.errCode === ErrorCodes.duplicatedEntity) {
      // Check if the error message contains any of the keys
      for (const [key, message] of Object.entries(fieldErrorMessages)) {
        if (error.message.includes(`(${key})`)) {
          return message;
        }
      }
    }
    return 'Something went wrong!';
  }
}
